import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { URL } from '../Helper/helper';
import { toast } from 'react-toastify';
import { invokeFailedBookings } from '../ServiceCalls/ApiCalls';
import './FailedBookings.css';

function FailedBookings() {
    const navigate = useNavigate();
    const user_info = JSON.parse(localStorage.getItem('user'));
    const [failedBookings, setFailedBookings] = useState([]);
    const [allFailedBookings, setAllFailedBookings] = useState([]); // Store all data
    const [isLoading, setIsLoading] = useState(true);

    const [deletingItems, setDeletingItems] = useState(new Set());
    
    // Filter states
    const [usernameFilter, setUsernameFilter] = useState('');
    const [dateFilter, setDateFilter] = useState('');

    useEffect(() => {
        if (!user_info) {
            navigate('/');
            return;
        }
        fetchFailedBookings();
    }, []);

    const fetchFailedBookings = async () => {
        setIsLoading(true);
        try {
            // First fetch all failed bookings from database
            const allFailedBookings = await invokeFailedBookings();
            console.log('All failed bookings received:', allFailedBookings);
            console.log('Type of allFailedBookings:', typeof allFailedBookings);
            console.log('Is array:', Array.isArray(allFailedBookings));

            // Ensure we have a valid array
            if (Array.isArray(allFailedBookings)) {
                setAllFailedBookings(allFailedBookings); // Store all data
                applyFilters(allFailedBookings); // Apply current filters
            } else {
                console.error('Expected array but received:', allFailedBookings);
                setAllFailedBookings([]);
                setFailedBookings([]); // Set empty array as fallback
                toast.error('Invalid data format received from server');
            }
        } catch (error) {
            console.error('Error fetching failed bookings:', error);
            toast.error('Failed to load failed bookings');
        } finally {
            setIsLoading(false);
        }
    };

    // Apply filters to the data
    const applyFilters = (data = allFailedBookings) => {
        let filteredData = [...data];

        // Filter by username
        if (usernameFilter.trim()) {
            filteredData = filteredData.filter(booking =>
                booking.userName && booking.userName.toLowerCase().includes(usernameFilter.toLowerCase())
            );
        }

        // Filter by booking date
        if (dateFilter) {
            filteredData = filteredData.filter(booking =>
                booking.booking_date === dateFilter
            );
        }

        setFailedBookings(filteredData);
    };

    // Handle filter changes
    const handleUsernameFilterChange = (e) => {
        setUsernameFilter(e.target.value);
    };

    const handleDateFilterChange = (e) => {
        setDateFilter(e.target.value);
    };

    const clearFilters = () => {
        setUsernameFilter('');
        setDateFilter('');
    };

    // Apply filters when filter values change
    useEffect(() => {
        applyFilters();
    }, [usernameFilter, dateFilter, allFailedBookings]);

    // Format date and time for display
    const formatDateTime = (booking) => {
        const date = new Date(booking.booking_date);
        const formattedDate = date.toLocaleDateString('en-AU', {
            weekday: 'short',
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
        return formattedDate;
    };



    const handleDeleteBooking = async (index) => {
        const newDeletingItems = new Set(deletingItems);
        newDeletingItems.add(index);
        setDeletingItems(newDeletingItems);

        try {
            // Remove from failed bookings list
            const updatedBookings = failedBookings.filter((_, i) => i !== index);
            setFailedBookings(updatedBookings);
            toast.success('Failed booking removed');
        } catch (error) {
            console.error('Error deleting booking:', error);
            toast.error('Error removing booking');
        } finally {
            const newDeletingItemsAfter = new Set(deletingItems);
            newDeletingItemsAfter.delete(index);
            setDeletingItems(newDeletingItemsAfter);
        }
    };



    if (isLoading) {
        return (
            <div className="wrapper">
                <div className="container d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                    <div className="text-center">
                        <div className="spinner-border text-warning" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-3 text-muted">Loading failed bookings...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="wrapper">
            <div className="container">
                <div className="row">
                    <div className="col-12">
                        <div className="failed-bookings-header d-flex justify-content-between align-items-center mb-4">
                            <div>
                                <h3 className="mb-1">
                                    <i className="fa fa-exclamation-triangle text-warning me-2"></i>
                                    All Failed Bookings ({failedBookings.length})
                                </h3>
                                <p className="text-muted">Manage all failed booking attempts from all users</p>
                            </div>
                            <div className="failed-bookings-actions">
                                <button
                                    className="btn btn-outline-secondary me-2"
                                    onClick={fetchFailedBookings}
                                    disabled={isLoading}
                                >
                                    <i className="fa fa-refresh me-1"></i>
                                    Refresh
                                </button>
                                <button
                                    className="btn btn-primary"
                                    onClick={() => navigate('/user')}
                                >
                                    <i className="fa fa-arrow-left me-1"></i>
                                    Back to Dashboard
                                </button>
                            </div>
                        </div>

                        {/* Filter Section */}
                        <div className="mb-4">
                            <div className="table-responsive">
                                <div className="d-flex align-items-center gap-3 mb-3" style={{padding: '1rem', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '0.375rem'}}>
                                    <input
                                        type="text"
                                        id="usernameFilter"
                                        className="form-control"
                                        placeholder="Enter username..."
                                        value={usernameFilter}
                                        onChange={handleUsernameFilterChange}
                                        style={{flex: '1'}}
                                    />
                                    <input
                                        type="date"
                                        id="dateFilter"
                                        className="form-control"
                                        value={dateFilter}
                                        onChange={handleDateFilterChange}
                                        style={{flex: '1'}}
                                    />
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary"
                                        onClick={clearFilters}
                                        disabled={!usernameFilter && !dateFilter}
                                        style={{minWidth: '120px'}}
                                    >
                                        Clear Filters
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-outline-info"
                                        onClick={() => applyFilters()}
                                        style={{minWidth: '120px'}}
                                    >
                                        Apply Filters
                                    </button>
                                </div>
                                
                                {/* Filter Summary */}
                                {(usernameFilter || dateFilter) && (
                                    <div className="mb-3" style={{padding: '0.5rem 1rem', backgroundColor: '#e9ecef', borderRadius: '0.375rem'}}>
                                        <small className="text-muted">
                                            Active Filters: 
                                            {usernameFilter && (
                                                <span className="badge bg-primary ms-1 me-1">
                                                    Username: "{usernameFilter}"
                                                </span>
                                            )}
                                            {dateFilter && (
                                                <span className="badge bg-success ms-1 me-1">
                                                    Date: {dateFilter}
                                                </span>
                                            )}
                                            | Showing {failedBookings.length} of {allFailedBookings.length} records
                                        </small>
                                    </div>
                                )}
                            </div>
                        </div>

                        {failedBookings.length === 0 && !isLoading ? (
                            <div className="alert alert-info text-center">
                                <i className="fa fa-search fa-3x text-info mb-3"></i>
                                <h4>
                                    {allFailedBookings.length === 0 ? 'No Failed Bookings!' : 'No Results Found!'}
                                </h4>
                                <p className="mb-3">
                                    {allFailedBookings.length === 0 
                                        ? 'Great news! There are no failed bookings in the system at the moment.'
                                        : 'No failed bookings match your current filter criteria. Try adjusting your filters.'
                                    }
                                </p>
                                <div className="d-flex justify-content-center gap-2">
                                    <button
                                        className="btn btn-primary"
                                        onClick={() => navigate('/bookings')}
                                    >
                                        <i className="fa fa-plus me-1"></i>
                                        Make New Booking
                                    </button>
                                    <button
                                        className="btn btn-outline-primary"
                                        onClick={() => navigate('/user')}
                                    >
                                        Back to Dashboard
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="table-responsive">
                                    <table className='table table-bordered failed-bookings-table'>
                                        <thead className="table-warning text-center">
                                            <tr>
                                                <th>S.No</th>
                                                <th>User</th>
                                                <th>Booking Date</th>
                                                <th>Court</th>
                                                <th>Time</th>
                                                <th>Price</th>
                                                <th>Status</th>
                                                <th>Error</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {Array.isArray(failedBookings) && failedBookings.map((booking, index) => (
                                                <tr key={index}>
                                                    <td>{index + 1}</td>
                                                    <td>
                                                        <div>
                                                            <strong>{booking.userName}</strong>
                                                            <br />
                                                            <small className="text-muted">
                                                                {booking.firstName} {booking.lastName}
                                                            </small>
                                                        </div>
                                                    </td>
                                                    <td>{formatDateTime(booking)}</td>
                                                    <td>
                                                        <span className="badge bg-primary">
                                                            {booking.booking_item}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        {booking.booking_time_start} - {booking.booking_time_end}
                                                    </td>
                                                    <td>
                                                        <span className="text-success fw-bold">
                                                            ${parseFloat(booking.booking_price || 0).toFixed(2)}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span className="badge bg-danger">
                                                            {booking.booking_status || 'Failed'}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <small className="text-danger">
                                                            {booking.error || booking.errorMessage || 'Booking failed'}
                                                        </small>
                                                    </td>
                                                    <td className="text-center">
                                                        <button
                                                            onClick={() => handleDeleteBooking(index)}
                                                            disabled={deletingItems.has(index)}
                                                            className="btn btn-danger btn-sm"
                                                            title="Remove this failed booking"
                                                        >
                                                            {deletingItems.has(index) ? (
                                                                <i className="fa fa-spinner fa-spin"></i>
                                                            ) : (
                                                                <i className="fa fa-trash"></i>
                                                            )}
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                <div className="failed-bookings-footer mt-4">
                                    <div className="row">
                                        <div className="col-md-8">
                                            <div className="alert alert-info">
                                                <i className="fa fa-info-circle me-2"></i>
                                                <strong>Note:</strong> These are all failed bookings from all users in the system.
                                                You can remove them from this list.
                                            </div>
                                        </div>
                                        <div className="col-md-4 text-end">
                                            <div className="d-flex flex-column gap-2">
                                                <button
                                                    className="btn btn-outline-secondary btn-sm"
                                                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                                                >
                                                    <i className="fa fa-arrow-up me-1"></i>
                                                    Back to Top
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default FailedBookings;